import { Component, computed, DestroyRef, inject, model, signal } from '@angular/core';
import {
  CheckOutRegisterSessionMovement,
  ICashRegisterBalance,
  ICheckOutRegisterSessionPendingMovement,
  ICurrentCheckOutRegisterSession
} from '@vierkant-software/types__api';
import { DateTime } from 'luxon';
import { APIStates, AppService } from 'src/services/app.service';
import {
  balanceAmount,
  RegisterSessionStateService,
  isMovementSerialMismatch,
  isNotACashRegister,
  isOpenMovementError,
  isSessionGone,
  isUncertainOutcome
} from 'src/services/register-session-state.service';

export enum RegisterSessionAction {
  open = 'open',
  deposit = 'deposit',
  withdrawal = 'withdrawal',
  count = 'count',
  close = 'close',
}

enum MovementNotice {
  changed = 'changed',
  pendingFinished = 'pendingFinished',
  pendingFinishedOwnOpen = 'pendingFinishedOwnOpen',
}

interface ICountResult {
  expected?: number;
  counted?: number;
  difference?: number;
  /** close only: what stays in the drawer as Wechselgeld, and what goes to the vault */
  remaining?: number;
  toVault?: number;
}

@Component({
  selector:    'checkout-register-session-dialog',
  templateUrl: './checkout-register-session-dialog.component.haml',
  styleUrls:   ['./checkout-register-session-dialog.component.sass'],
  standalone:  false
})
export class CheckoutRegisterSessionDialogComponent {
  private app = inject(AppService);
  registerSession = inject(RegisterSessionStateService);

  protected readonly Notice = MovementNotice;
  visible = model(false);
  action = signal<RegisterSessionAction>(RegisterSessionAction.open);
  balance = signal<ICashRegisterBalance | null>(null);
  busy = signal(false);
  uncertain = signal(false);
  confirmClose = signal(false);
  // the opening difference the cashier confirmed; a changed count needs a new confirmation
  confirmedDifference = signal<number | undefined>(undefined);
  // from the state load of the open dialog: the Wechselgeld the last session left
  carryOver = signal(0);
  result = signal<ICountResult | null>(null);
  submittedBalance = signal<ICashRegisterBalance | null>(null);
  // One key per entered movement: a repeat with it books at most once, so a refused repeat is known unbooked.
  requestKey = signal<string | undefined>(undefined);
  // Taken together from one state load: when the dialog opens and after a mismatch, open movement or finish.
  movementSerial = signal<number | undefined>(undefined);
  openingPending = signal(false);
  pending = signal<ICheckOutRegisterSessionPendingMovement | undefined>(undefined);
  notice = signal<MovementNotice | null>(null);
  // The own entry of this dialog is known not to be booked (rejected before booking).
  ownUnbooked = signal(false);
  // the pre-fill of start(); only an entry of the cashier is worth a discard question
  private initialCount: ICashRegisterBalance | null = null;
  private discardQuestionOpen = false;

  // a close stuck in `closing` counted again instead of resumed
  recount = signal(false);
  resumeClose = computed(() => this.action() === RegisterSessionAction.close && this.registerSession.isClosing() && !this.recount());
  currency = computed(() => this.registerSession.session()?.currency ?? this.app.defaultCurrency);
  confirmCloseHint = computed(() => this.confirmClose() && !this.resumeClose());
  // a count that differs from the carry-over is booked as Plus- or Fehlbestand at the opening
  openingDifference = computed(() => this.action() === RegisterSessionAction.open && this.carryOver() > 0 && this.balance()
    ? this.amount() - this.carryOver() : undefined);
  // the backend keeps the configured Standard-Wechselgeld in the drawer, at most the count
  changeFloat = computed(() => {
    const float = this.registerSession.defaultOpening();
    return float ? balanceAmount(float) : 0;
  });
  closeSplit = computed(() => {
    if (this.action() !== RegisterSessionAction.close || this.resumeClose() || !this.balance())
      return undefined;
    const remaining = Math.min(this.amount(), this.changeFloat());
    return { remaining, toVault: this.amount() - remaining };
  });
  confirmOpen = computed(() => (this.openingDifference() ?? 0) !== 0 && this.confirmedDifference() === this.openingDifference());
  canFinishPending = computed(() => this.registerSession.canFinish(this.pending()?.movement));
  isMovement = computed(() => [RegisterSessionAction.deposit, RegisterSessionAction.withdrawal].includes(this.action()));
  submittedAmount = computed(() => {
    const balance = this.submittedBalance();
    return balance ? balanceAmount(balance) : undefined;
  });
  serialMissing = computed(() => this.isMovement() && !!this.balance() && this.movementSerial() === undefined);
  sessionNotOpen = computed(() => this.serialMissing() && this.registerSession.loaded() && !this.registerSession.loadFailed()
    && !this.registerSession.isOpen());
  // without a fresh state neither the serial nor a started close is known
  stateMissing = computed(() => this.registerSession.loadFailed() || (this.serialMissing() && !this.sessionNotOpen()));
  showPending = computed(() => this.isMovement() && !!this.pending() && !this.uncertain());
  showForm = computed(() => !this.uncertain() && !this.result() && !this.showPending());
  // Count and close only inform; the close books an open movement itself.
  pendingHintKey = computed(() => {
    if (!this.registerSession.pendingMovement())
      return undefined;
    if (this.action() === RegisterSessionAction.count)
      return 'CHECKOUT.REGISTER_SESSION.PENDING.COUNT_HINT';
    if (this.action() === RegisterSessionAction.close)
      return 'CHECKOUT.REGISTER_SESSION.PENDING.CLOSE_HINT';
    return undefined;
  });
  pendingTypeKey = computed(() => this.pending()?.movement === CheckOutRegisterSessionMovement.withdrawal
    ? 'CHECKOUT.REGISTER_SESSION.TITLE.WITHDRAWAL' : 'CHECKOUT.REGISTER_SESSION.TITLE.DEPOSIT');
  pendingSourceKey = computed(() => this.pending()?.fromThisTill
    ? 'CHECKOUT.REGISTER_SESSION.PENDING.THIS_TILL' : 'CHECKOUT.REGISTER_SESSION.PENDING.OTHER_TILL');
  pendingFinishKey = computed(() => this.pending()?.movement === CheckOutRegisterSessionMovement.withdrawal
    ? 'CHECKOUT.REGISTER_SESSION.SUBMIT.FINISH_PENDING_WITHDRAWAL' : 'CHECKOUT.REGISTER_SESSION.SUBMIT.FINISH_PENDING_DEPOSIT');
  pendingAtKey = computed(() => this.pending()?.at?.hasSame(DateTime.serverTime(), 'day')
    ? 'CHECKOUT.REGISTER_SESSION.PENDING.AT_TODAY' : 'CHECKOUT.REGISTER_SESSION.PENDING.AT_DAY');
  amount = computed(() => {
    const balance = this.balance();
    return balance ? balanceAmount(balance) : 0;
  });
  canSubmit = computed(() => !!this.balance() && !this.busy() && !this.registerSession.loadFailed()
    && !(this.isMovement() && (this.amount() === 0 || this.movementSerial() === undefined)));
  titleKey = computed(() => `CHECKOUT.REGISTER_SESSION.TITLE.${this.action().toUpperCase()}`);
  balanceLabel = computed(() => `CHECKOUT.REGISTER_SESSION.BALANCE.${this.action().toUpperCase()}`);
  totalLabel = computed(() => `CHECKOUT.REGISTER_SESSION.TOTAL.${this.action().toUpperCase()}`);
  submitLabel = computed(() => {
    if (this.resumeClose())
      return 'CHECKOUT.REGISTER_SESSION.SUBMIT.RESUME_CLOSE';
    if (this.recount() && !this.confirmClose())
      return 'CHECKOUT.REGISTER_SESSION.SUBMIT.RECOUNT';
    if (this.confirmClose())
      return 'CHECKOUT.REGISTER_SESSION.SUBMIT.CONFIRM_CLOSE';
    if (this.confirmOpen())
      return 'CHECKOUT.REGISTER_SESSION.SUBMIT.CONFIRM_OPEN';
    return `CHECKOUT.REGISTER_SESSION.SUBMIT.${this.action().toUpperCase()}`;
  });

  constructor() {
    // A half entered count must not be booked under the next cashier.
    const subscription = this.app.$APIState.subscribe(state => {
      if (state.type === APIStates.loggedIn || state.type === APIStates.loggedOut) {
        // an open discard question would otherwise close the next cashier's dialog; close() calls no reject
        if (this.discardQuestionOpen) {
          this.app.confirmationService.close();
          this.discardQuestionOpen = false;
        }
        this.visible.set(false);
      }
    });
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());
  }

  async start(action: RegisterSessionAction, recount = false): Promise<void> {
    this.action.set(action);
    this.recount.set(recount && action === RegisterSessionAction.close);
    this.balance.set(null);
    this.uncertain.set(false);
    this.confirmClose.set(false);
    this.confirmedDifference.set(undefined);
    this.carryOver.set(0);
    this.result.set(null);
    this.submittedBalance.set(null);
    this.requestKey.set(undefined);
    this.adopt(undefined);
    this.notice.set(null);
    this.ownUnbooked.set(false);
    this.initialCount = null;
    this.visible.set(true);

    const state = await this.registerSession.reload();
    if (this.isMovement())
      this.adopt(state);
    // the pre-fill is the configured Wechselgeld; confirmed unchecked against another carry-over it would book a difference
    const carryOver = action === RegisterSessionAction.open ? state?.carryOver ?? 0 : 0;
    this.carryOver.set(carryOver);
    const float = action === RegisterSessionAction.open ? state?.defaultOpening : undefined;
    const opening = float && (carryOver === 0 || balanceAmount(float) === carryOver) ? float : undefined;
    this.initialCount = {
      coins:       { ...opening?.coins },
      bills:       { ...opening?.bills },
      currency:    this.currency(),
      totalAmount: 0,
    };
    this.balance.set(this.initialCount);
  }

  // A retry sends the same entry with the same key and serial; the backend books it at most once.
  async retry(): Promise<void> {
    const balance = this.submittedBalance();
    const movementSerial = this.movementSerial();
    const requestKey = this.requestKey();
    if (!balance || movementSerial === undefined || !requestKey || this.busy())
      return;
    this.busy.set(true);
    try {
      await this.bookMovement(balance, movementSerial, requestKey);
      this.visible.set(false);
    } catch(e) {
      if (isSessionGone(e)) {
        this.visible.set(false);
      } else if (isOpenMovementError(e) || isMovementSerialMismatch(e)) {
        // A booked entry would have been recognized by its key, so the own entry is not booked.
        this.adopt(await this.registerSession.reloadAfterAction());
        this.uncertain.set(false);
        this.ownUnbooked.set(true);
        if (!this.pending())
          this.notice.set(MovementNotice.changed);
      }
    } finally {
      this.busy.set(false);
    }
  }

  async submit(): Promise<void> {
    const balance = this.balance();
    if (!balance || !this.canSubmit())
      return;
    if (this.action() === RegisterSessionAction.close && !this.resumeClose() && !this.confirmClose()) {
      this.confirmClose.set(true);
      return;
    }
    if ((this.openingDifference() ?? 0) !== 0 && !this.confirmOpen()) {
      this.confirmedDifference.set(this.openingDifference());
      return;
    }
    let requestKey: string | undefined;
    try {
      requestKey = this.isMovement() ? crypto.randomUUID() : undefined;
    } catch(e) {
      // not an RPC error, run() does not show it; nothing was sent
      e.displayErrorToast?.();
      return;
    }

    this.busy.set(true);
    try {
      if (this.isMovement()) {
        this.submittedBalance.set(balance);
        this.requestKey.set(requestKey);
        this.notice.set(null);
        this.ownUnbooked.set(false);
      }
      switch(this.action()) {
        case RegisterSessionAction.open:
          await this.registerSession.open(balance);
          this.visible.set(false);
          break;
        case RegisterSessionAction.deposit:
        case RegisterSessionAction.withdrawal:
          await this.bookMovement(balance, this.movementSerial()!, this.requestKey()!);
          this.visible.set(false);
          break;
        case RegisterSessionAction.count: {
          const count = await this.registerSession.count(balance);
          this.result.set({ expected: count.expected, counted: count.counted, difference: count.difference });
          break;
        }
        case RegisterSessionAction.close: {
          const closed = await this.registerSession.close(balance, this.recount());
          const remaining = closed.remainingAmount ?? 0;
          this.result.set({
            expected:   closed.expected,
            counted:    closed.countedAmount,
            difference: closed.difference,
            remaining,
            toVault:    (closed.countedAmount ?? 0) - remaining,
          });
          break;
        }
      }
    } catch(e) {
      if (isUncertainOutcome(e)) {
        this.uncertain.set(true);
      } else if (isNotACashRegister(e) || isSessionGone(e)) {
        this.visible.set(false);
      } else if (isMovementSerialMismatch(e) || isOpenMovementError(e)) {
        // Nothing was booked; the cashier decides after checking this very load.
        this.adopt(await this.registerSession.reloadAfterAction());
        this.ownUnbooked.set(true);
        if (!this.pending())
          this.notice.set(MovementNotice.changed);
      }
    } finally {
      this.busy.set(false);
    }
  }

  // Books exactly the open movement shown; the own entry stays unbooked and the form comes back.
  async finishPending(): Promise<void> {
    const movementSerial = this.movementSerial();
    if (movementSerial === undefined || this.busy())
      return;
    this.busy.set(true);
    try {
      await this.registerSession.finishPendingMovement(movementSerial);
    } catch{
      // Every outcome is resolved by the state load below.
    }
    try {
      const state = await this.registerSession.reloadAfterAction();
      this.adopt(state);
      this.uncertain.set(false);
      this.submittedBalance.set(null);
      this.requestKey.set(undefined);
      this.notice.set(null);
      // Only a booking removes an open movement, whoever finished it.
      if (state && !this.pending())
        this.notice.set(this.ownUnbooked() ? MovementNotice.pendingFinishedOwnOpen : MovementNotice.pendingFinished);
    } finally {
      this.busy.set(false);
    }
  }

  private adopt(state: ICurrentCheckOutRegisterSession | undefined): void {
    this.movementSerial.set(state?.movementSerial);
    this.openingPending.set(!!state?.openingPending);
    this.pending.set(state?.pendingMovement);
  }

  private async bookMovement(balance: ICashRegisterBalance, movementSerial: number, requestKey: string): Promise<void> {
    if (this.action() === RegisterSessionAction.deposit)
      await this.registerSession.deposit(balance, movementSerial, requestKey);
    else
      await this.registerSession.withdraw(balance, movementSerial, requestKey);
  }

  // a changed count needs a new confirmation of the close
  onBalanceChange(balance: ICashRegisterBalance): void {
    this.balance.set(balance);
    this.confirmClose.set(false);
  }

  // a misclick must not discard an entered count, nor the key that makes "send again" safe
  closeDialog(): void {
    if (this.uncertain() && this.isMovement() && this.requestKey())
      this.askDiscard('CHECKOUT.REGISTER_SESSION.DISCARD_UNCERTAIN');
    // an open movement hides the own unbooked entry, it comes back after finishing it
    else if ((this.showForm() || this.showPending()) && this.countChanged())
      this.askDiscard('CHECKOUT.REGISTER_SESSION.DISCARD');
    else
      this.visible.set(false);
  }

  private askDiscard(texts: string): void {
    this.discardQuestionOpen = true;
    this.app.confirmationService.confirm({
      key:         'confirmDialog',
      header:      this.app.vtranslate.instant(`${texts}.HEADER`),
      message:     this.app.vtranslate.instant(`${texts}.MESSAGE`),
      icon:        'pi pi-exclamation-triangle',
      acceptLabel: this.app.vtranslate.instant(`${texts}.ACCEPT`),
      rejectLabel: this.app.vtranslate.instant(`${texts}.REJECT`),
      accept:      () => {
        this.discardQuestionOpen = false;
        this.visible.set(false);
      },
      // without it the flag stays set after the reject button
      reject: () => {
        this.discardQuestionOpen = false;
      },
    });
  }

  private countChanged(): boolean {
    const balance = this.balance();
    if (!balance)
      return false;
    const amounts = (entries: Record<number, { amount: number }> | undefined) =>
      new Map(Object.entries(entries ?? {}).map(([key, entry]) => [key, entry?.amount ?? 0]));
    const differs = (current: Record<number, { amount: number }>, initial: Record<number, { amount: number }> | undefined) => {
      const now = amounts(current);
      const before = amounts(initial);
      return [...new Set([...now.keys(), ...before.keys()])].some(key => (now.get(key) ?? 0) !== (before.get(key) ?? 0));
    };
    return differs(balance.coins, this.initialCount?.coins) || differs(balance.bills, this.initialCount?.bills);
  }
}
